import React, { useEffect, useRef, useCallback, useState, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { MovieContext } from '../components/context/Movies.context';
import tmdbService from '../services/tmdb';
import {
  PLAYERS,
  buildPlayerUrl,
  getStoredPlayerId,
  savePlayerId,
} from '../services/players';
import {
  isFullscreen,
  requestFullscreenSafe,
  exitFullscreenSafe,
  setTvNavigationEnabled,
} from '../components/common/TvNavigation';

const SEEK_STEP = 10;
const VOLUME_STEP = 10;

// Ask the device to rotate to landscape while the player is open.
// Requires fullscreen (browser policy); fails silently where unsupported.
const requestLandscapeOrientation = async () => {
  try {
    const orientation = typeof window !== 'undefined' && window.screen?.orientation;
    if (orientation?.lock) {
      await orientation.lock('landscape');
      return true;
    }
  } catch {
    /* not supported / denied */
  }
  return false;
};

const unlockOrientation = () => {
  try {
    const orientation = typeof window !== 'undefined' && window.screen?.orientation;
    if (orientation?.unlock) {
      orientation.unlock();
    }
  } catch {
    /* no-op */
  }
};

const WatchPlayerPage = () => {
  const { id, season = '1', episode = '1' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const frameRef = useRef(null);
  const stateRef = useRef({ seconds: 0, duration: 0, volume: 100, playing: false });
  // The TV receiver passes ?player=<id> so a remote player switch survives the
  // cross-origin APK iframe reload (localStorage is not selectable remotely).
  const playerFromUrl = new URLSearchParams(location.search).get('player');
  const [playerId, setPlayerId] = useState(() =>
    playerFromUrl ? savePlayerId(playerFromUrl) : getStoredPlayerId()
  );
  const [showBufferTip, setShowBufferTip] = useState(true);
  const [slowNetwork] = useState(() => {
    try {
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (!conn) return false;
      if (conn.saveData) return true;
      return ['slow-2g', '2g', '3g'].includes(conn.effectiveType);
    } catch (err) {
      return false;
    }
  });

  useEffect(() => {
    if (playerFromUrl) setPlayerId(savePlayerId(playerFromUrl));
  }, [playerFromUrl]);

  // The embed player owns the keyboard (FF/BW, volume, play-pause). Focus the
  // iframe after every load so those keys reach the player instead of the app.
  const focusPlayer = useCallback(() => {
    const frame = frameRef.current;
    if (!frame) return;
    try {
      frame.focus({ preventScroll: true });
    } catch (err) {
      try {
        frame.focus();
      } catch (e) {
        /* no-op */
      }
    }
    try {
      if (frame.contentWindow) frame.contentWindow.focus();
    } catch (err) {
      /* cross-origin focus can throw */
    }
  }, []);

  // While the player is open, the global D-pad engine must not move focus to
  // the overlay buttons — that is what made the player lose FF/BW keys.
  // Back handling stays active inside TvNavigation.
  useEffect(() => {
    setTvNavigationEnabled(false);
    return () => setTvNavigationEnabled(true);
  }, []);

  // The media type lives in the URL path (/watch/tv/... vs /watch/movie/...);
  // the route patterns define no :type param, so reading a `type` from useParams
  // always came back undefined and silently played every series as a movie.
  const mediaType = location.pathname.startsWith('/watch/tv/') ? 'tv' : 'movie';
  const src = buildPlayerUrl(
    { id },
    { mediaType, season: Number(season), episode: Number(episode), playerId }
  );

  // Re-focus after a player switch / iframe reload (src changes with key={src}).
  useEffect(() => {
    const timer = setTimeout(focusPlayer, 400);
    return () => clearTimeout(timer);
  }, [src, focusPlayer]);

  // --- Continue-watching (syncs across phone/TV via the account) ---
  const [meta, setMeta] = useState(null);
  const { upsertContinueWatching } = useContext(MovieContext);

  // Fetch title/posters for the Continue Watching row.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const details =
          mediaType === 'tv'
            ? await tmdbService.getTVDetails(id)
            : await tmdbService.getMovieDetails(id);
        if (!cancelled) setMeta(details || null);
      } catch (e) {
        if (!cancelled) setMeta(null);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [mediaType, id]);

  const persistProgress = useCallback(() => {
    const s = stateRef.current;
    if (!s || s.seconds < 20) return; // ignore accidental taps
    upsertContinueWatching({
      id,
      media_type: mediaType,
      title: meta?.title || meta?.name || meta?.original_title || meta?.original_name || '',
      poster_path: meta?.poster_path || null,
      backdrop_path: meta?.backdrop_path || null,
      season: Number(season) || 1,
      episode: Number(episode) || 1,
      seconds: s.seconds,
      duration: s.duration,
      updatedAt: new Date().toISOString(),
    });
  }, [id, mediaType, meta, season, episode, upsertContinueWatching]);

  // Save every 10s while watching + one final save on unmount.
  useEffect(() => {
    const timer = setInterval(persistProgress, 10000);
    return () => {
      clearInterval(timer);
      persistProgress();
    };
  }, [persistProgress]);

  // Enter fullscreen + lock landscape while watching (mobile auto-rotate fix).
  // Re-asserts on visibility change because Android WebView can drop the lock.
  //
  // TV receiver isse apne iframe me embed karta hai (same-origin). Nested page
  // ko fullscreen/orientation-lock maangna bekaar hai — kuch TV WebViews uspe
  // screen blank kar dete hain. Embedded ho to bilkul skip karo; receiver
  // already fullscreen hai.
  useEffect(() => {
    const isEmbedded = window.self !== window.top;
    if (isEmbedded) return undefined;

    let orientationLocked = false;
    const enterImmersive = async () => {
      if (!isFullscreen()) await requestFullscreenSafe();
      orientationLocked = await requestLandscapeOrientation();
    };
    enterImmersive();
    const onVisible = () => {
      if (document.visibilityState === 'visible') enterImmersive();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      exitFullscreenSafe();
      if (orientationLocked) unlockOrientation();
    };
  }, []);

  const switchPlayer = useCallback(
    (nextPlayerId) => {
      const saved = savePlayerId(nextPlayerId);
      if (saved === playerId) return;
      stateRef.current = { seconds: 0, duration: 0, volume: 100, playing: false };
      setPlayerId(saved);
    },
    [playerId]
  );

  const sendToPlayer = useCallback((payload) => {
    try {
      if (frameRef.current && frameRef.current.contentWindow) {
        frameRef.current.contentWindow.postMessage(payload, '*');
      }
    } catch {
      /* cross-origin */
    }
    try {
      if (window.EmbedMasterPlayer && window.EmbedMasterPlayer.postMessage) {
        window.EmbedMasterPlayer.postMessage(payload, '*');
      }
    } catch {
      /* bridge unavailable */
    }
  }, []);

  const sendNative = useCallback(
    (payload) => sendToPlayer({ ...payload, cmd: true }),
    [sendToPlayer]
  );

  const sendCommand = useCallback(
    (command, value) => sendToPlayer({ source: 'embed_player_command', command, value }),
    [sendToPlayer]
  );

  const togglePlay = useCallback(() => {
    stateRef.current.playing = !stateRef.current.playing;
    if (stateRef.current.playing) {
      sendNative({ api: 'play' });
      sendCommand('play');
    } else {
      sendNative({ api: 'pause' });
      sendCommand('pause');
    }
  }, [sendCommand, sendNative]);

  const seekBy = useCallback((delta) => {
    const s = stateRef.current;
    let target = Math.max(0, s.seconds + delta);
    if (s.duration > 0) target = Math.min(s.duration, target);
    sendNative({ api: 'seek', set: target });
    sendCommand('seek', target);
    s.seconds = target;
  }, [sendCommand, sendNative]);

  const setVolumeBy = useCallback((delta) => {
    const s = stateRef.current;
    const v = Math.round(Math.min(100, Math.max(0, s.volume + delta)));
    s.volume = v;
    sendNative({ api: 'volume', set: v / 100 });
    sendCommand('volume', v);
    if (v === 0) sendCommand('mute');
  }, [sendCommand, sendNative]);

  // --- Remote-control executor -------------------------------------------
  // Phone (MQTT -> TvCastReceiver) aur TV D-pad dono yahan aate hain:
  // play/pause/seek/volume/episode + EmbedMaster ke settings commands
  // (HD/quality, VIP server, source, audio, subtitle).
  const applyRemoteAction = useCallback((cmd) => {
    if (!cmd || !cmd.type) return;
    const t = cmd.type;
    if (t === 'pause') {
      stateRef.current.playing = false;
      sendNative({ api: 'pause' });
      sendCommand('pause');
    } else if (t === 'resume' || t === 'play') {
      stateRef.current.playing = true;
      sendNative({ api: 'play' });
      sendCommand('play');
    } else if (t === 'seek') {
      const target = Math.max(0, Number(cmd.value) || 0);
      sendNative({ api: 'seek', set: target });
      sendCommand('seek', target);
      stateRef.current.seconds = target;
    } else if (t === 'seekBy') {
      seekBy(Number(cmd.value) || 0);
    } else if (t === 'vol') {
      const v = Math.round(Math.min(100, Math.max(0, Number(cmd.value) || 0)));
      stateRef.current.volume = v;
      sendNative({ api: 'volume', set: v / 100 });
      sendCommand('volume', v);
    } else if (t === 'mute') {
      sendNative({ api: 'mute' });
      sendCommand('mute');
    } else if (t === 'unmute') {
      sendNative({ api: 'unmute' });
      sendCommand('unmute');
    } else if (t === 'player') {
      switchPlayer(cmd.value);
    } else if (t === 'episode' || t === 'source' || t === 'server' ||
               t === 'quality' || t === 'hd' || t === 'audio' ||
               t === 'subtitle' || t === 'subtitleOff' || t === 'nextSource') {
      // Player-level settings = fresh embed load. TV app isi route pe hai,
      // isliye navigate() se season/episode/source params re-resolve hote hain.
      const targetSeason = cmd.season ?? season;
      const targetEpisode = cmd.episode ?? episode;
      if (t === 'episode') {
        navigate(`/watch/tv/${id}/${Number(targetSeason) || 1}/${Number(targetEpisode) || 1}`, { replace: true });
      } else {
        // source/server/quality/audio embed player ke apne controls hain —
        // player ko command forward karo (EmbedMaster API convention).
        sendCommand(t === 'nextSource' ? 'nextSource' : t, cmd.value ?? cmd.n);
      }
    }
  }, [sendCommand, sendNative, seekBy, navigate, id, season, episode, switchPlayer]);

  // Live state snapshot for the phone remote (published via MQTT by
  // TvCastReceiver on command + heartbeat).
  useEffect(() => {
    window.__fwPlayerApi = {
      action: applyRemoteAction,
      state: () => ({
        playing: stateRef.current.playing,
        position: stateRef.current.seconds,
        duration: stateRef.current.duration,
        volume: stateRef.current.volume,
        muted: stateRef.current.volume === 0,
        path: location.pathname,
        season: Number(season) || 1,
        episode: Number(episode) || 1,
        player: playerId,
        ts: Date.now(),
      }),
    };
    return () => {
      try { delete window.__fwPlayerApi; } catch (e) { /* noop */ }
    };
  }, [applyRemoteAction, location.pathname, season, episode, playerId]);

  // Cross-origin APK: the receiver cannot read __fwPlayerApi, so broadcast the
  // same snapshot to the parent every second. receiver.html merges it into the
  // state it publishes to the phone remote.
  useEffect(() => {
    const post = () => {
      try {
        if (!window.parent || window.parent === window) return;
        const api = window.__fwPlayerApi;
        if (!api || typeof api.state !== 'function') return;
        window.parent.postMessage(
          { source: 'flexwatch-app', type: 'player-state', state: api.state() },
          '*'
        );
      } catch (e) { /* noop */ }
    };
    post();
    const timer = window.setInterval(post, 1000);
    return () => window.clearInterval(timer);
  }, [playerId]);

  // Commands relayed by the receiver via postMessage (cross-origin fallback).
  // Same-origin hosting calls __fwPlayerApi.action directly, so this branch is
  // only reached in the APK/odd-origin case.
  useEffect(() => {
    const onRemoteCommand = (e) => {
      const d = e && e.data;
      if (!d || typeof d !== 'object') return;
      if (d.source !== 'flexwatch-remote') return;
      if (d.type === 'nav') return; // RemoteNavBridge owns navigation
      if (d.type === 'player') {
        switchPlayer(d.value);
        return;
      }
      applyRemoteAction(d);
    };
    window.addEventListener('message', onRemoteCommand);
    return () => window.removeEventListener('message', onRemoteCommand);
  }, [applyRemoteAction, switchPlayer]);

  // Track timeupdate/state events so seek/play-toggle have accurate state
  useEffect(() => {
    const onMessage = (e) => {
      let d = e && e.data;
      if (!d) return;
      if (typeof d === 'string') {
        try {
          d = JSON.parse(d);
        } catch (err) {
          return;
        }
      }
      if (!d || typeof d !== 'object') return;

      // Player 1 (EmbedMaster): { info: { seconds, duration } }
      // Player 2 (Vidy):        { event, currentTime, duration }
      const info = d.info || d.data || {};
      const seconds = typeof info.seconds === 'number' ? info.seconds : d.currentTime;
      const duration = typeof info.duration === 'number' ? info.duration : d.duration;
      const evt = d.event || d.event_name || null;

      if (typeof seconds === 'number') stateRef.current.seconds = seconds;
      if (typeof duration === 'number' && duration > 0) stateRef.current.duration = duration;
      if (evt === 'playing' || evt === 'play') stateRef.current.playing = true;
      if (evt === 'paused' || evt === 'pause' || evt === 'ended') stateRef.current.playing = false;
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // D-pad shortcuts: Left/Right seek ±10s, Up/Down volume, OK/Space play-pause.
  //
  // Player 2 (vidy.st) has no inbound command API, so the parent must never
  // swallow its keys: hand focus back to the iframe and let the player's own
  // FF/BW shortcuts handle the next press. Player 1 (EmbedMaster) supports the
  // command API, so its shortcuts stay here.
  useEffect(() => {
    const isEmbedMaster = playerId === 'embedmaster';
    const PLAYER_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' ', 'Enter'];

    const onKey = (e) => {
      if (document.activeElement === frameRef.current) return; // key belongs to the player

      if (!isEmbedMaster) {
        if (PLAYER_KEYS.includes(e.key)) focusPlayer();
        return;
      }

      const k = e.key;
      const code = e.keyCode || e.which || 0;
      if (k === 'ArrowLeft' || code === 37) {
        e.preventDefault();
        seekBy(-SEEK_STEP);
      } else if (k === 'ArrowRight' || code === 39) {
        e.preventDefault();
        seekBy(SEEK_STEP);
      } else if (k === 'ArrowUp' || code === 38) {
        e.preventDefault();
        setVolumeBy(VOLUME_STEP);
      } else if (k === 'ArrowDown' || code === 40) {
        e.preventDefault();
        setVolumeBy(-VOLUME_STEP);
      } else if (k === ' ' || code === 32 || k === 'Enter' || code === 13 || code === 23) {
        e.preventDefault();
        togglePlay();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [playerId, focusPlayer, seekBy, setVolumeBy, togglePlay]);

  const goBack = () => {
    exitFullscreenSafe();
    navigate(-1);
  };

  return (
    <div
      className="fixed inset-0 bg-black overflow-hidden"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        minWidth: '100%',
        minHeight: '100%',
        zIndex: 999999,
        background: '#000000',
        overflow: 'hidden',
      }}
    >
      {src ? (
        <>
          <iframe
            key={src}
            ref={frameRef}
            src={src}
            title="ShadowCinema Player"
            className="w-full h-full border-0"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              minWidth: '100%',
              minHeight: '100%',
              border: 0,
              outline: 'none',
            }}
            frameBorder="0"
            allow="autoplay *; fullscreen *; picture-in-picture *; encrypted-media *"
            allowFullScreen
            onLoad={focusPlayer}
            onError={() => {
              exitFullscreenSafe();
              navigate(-1);
            }}
          />

          <div className="absolute top-0 left-0 right-0 z-[1000000] flex items-center justify-between gap-2 p-3 pointer-events-none">
            <button
              type="button"
              onClick={goBack}
              className="pointer-events-auto inline-flex items-center gap-1.5 bg-black/70 hover:bg-black/85 text-white text-xs font-semibold px-3 py-2 rounded-xl border border-white/15 backdrop-blur-md transition-colors"
            >
              <span aria-hidden="true">←</span>
              <span>Back</span>
            </button>

            <div className="pointer-events-auto flex items-center gap-1 bg-black/70 border border-white/15 rounded-xl p-1 backdrop-blur-md">
              {PLAYERS.map((player) => (
                <button
                  key={player.id}
                  type="button"
                  data-focusable
                  onClick={() => switchPlayer(player.id)}
                  aria-pressed={playerId === player.id}
                  title={`${player.name} — ${player.label}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                    playerId === player.id
                      ? 'bg-accent-gold text-dark-900'
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {player.name}
                </button>
              ))}
            </div>
          </div>

          {showBufferTip && (
            <div className="absolute bottom-4 left-3 right-3 z-[1000001] pointer-events-none">
              <div className="pointer-events-auto flex items-start justify-between gap-3 bg-black/75 backdrop-blur-md border border-white/15 rounded-xl px-3 py-2 text-[11px] leading-snug text-gray-200">
                <p>
                  <span className="font-bold text-accent-gold">Buffering?</span>{' '}
                  {slowNetwork
                    ? 'Slow network detected — switch Player 1/2/3 and use 720p or SD in the player settings.'
                    : 'Switch Player 1/2/3 or lower the quality in the player settings.'}
                </p>
                <button
                  type="button"
                  onClick={() => setShowBufferTip(false)}
                  aria-label="Dismiss buffering tip"
                  className="shrink-0 text-gray-400 hover:text-white text-base leading-none px-1"
                >
                  ×
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-4 text-gray-400">
          <p className="text-sm">Player failed to load.</p>
          <button
            onClick={goBack}
            className="bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-4 py-2 rounded-xl border border-white/10 transition-colors"
          >
            Go Back
          </button>
        </div>
      )}
    </div>
  );
};

export default WatchPlayerPage;